import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import clsx from 'clsx';
import Moment from 'moment';
import { Skeleton } from '@material-ui/lab';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import { Button, IconButton, Typography, makeStyles } from '@material-ui/core';

import { hasPermission } from '../../../Utils/helper';
import { CalendarEmpty, NewEntryNoti } from '../../../../constants';
import { getAppointments } from '../../../../actions/appointment/appointment-actions';

const useStyles = makeStyles(() => ({
  background__style: {
    background: '#FFFFFF',
    borderRadius: '20px',
    height: '100%',
  },
  space__between: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  header: {
    padding: '1rem',
  },
  heading: {
    fontWeight: '600',
    fontSize: '14px',
    lineHeight: '19px',
    color: '#6d7d8b',
    marginBottom: '.3rem',
  },
  amount: {
    fontWeight: 'bold',
    fontSize: '20px',
    lineHeight: '35px',
    color: '#29264a',
  },
  empty__screen: {
    padding: '2rem 1rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
  empty__icon: {
    height: '115px',
    width: '150px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& svg': {
      width: '80%',
    },
  },
  empty__text: {
    color: '#ACC1D4',
    fontSize: '14px',
    textAlign: 'center',
    marginTop: '2rem',
    width: '95%',
  },
  empty__book__btn: {
    background: 'rgba(255, 96, 25, .3) !important',
    color: 'var(--color-secondary)',
    borderRadius: '100px',
    padding: '0.74rem 1.5rem',
    textTransform: 'capitalize',
    marginTop: '2.2rem',
    boxShadow: 'none',
    '& svg': {
      '& path': {
        stroke: 'var(--color-secondary)',
      },
    },
  },
  date__select__container: {
    padding: '0 1rem 1rem',
  },
  arrow__icon: {
    padding: '5px',
  },
  date__day: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '10px',
    width: '35px',
    height: '60px',
    margin: '0 .3rem',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--color-secondary)',
      '& .bar__texts': {
        color: '#fff',
      },
    },
  },
  bar__day: {
    fontWeight: 'normal',
    fontSize: '14px',
    lineHeight: '17px',
    letterSpacing: '-0.512518px',
    color: '#8f92a1',
    paddingTop: '.3rem',
  },
  bar__date: {
    fontWeight: '600',
    fontSize: '16px',
    lineHeight: '19px',
    letterSpacing: '-0.512518px',
    color: '#171717',
  },
  selected__date__day: {
    background: 'var(--color-secondary)',
    '& .bar__texts': {
      color: '#fff',
    },
  },
  content__main: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 155px)',
    overflowY: 'auto',
  },
  inner__div: {
    height: '7.3rem',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    borderTop: '1px solid rgba(109, 125, 139, 0.08)',
    '&:last-child': {
      borderBottom: '1px solid rgba(109, 125, 139, 0.08)',
    },
  },
  time__slot: {
    display: 'flex',
    height: '100%',
    padding: '0 .6rem',
    borderRight: '1px solid rgba(109, 125, 139, 0.08)',
  },
  time__slot__content: {
    transform: 'rotate(-90deg)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    '& .time__text': {
      fontWeight: '600',
      fontSize: '13px',
      lineHeight: '16px',
    },
  },
  time__up: {
    color: '#6d7d8b',
  },
  time__down: {
    color: 'rgba(41, 38, 74, 0.2)',
  },
  separator: {
    width: '40%',
    padding: '1.5px 15px',
    borderRadius: '50px',
    margin: '5px 0',
    alignSelf: 'center',
    background: 'var(--color-primary)',
  },
  appointment__content: {
    padding: '1rem',
    width: 'calc(100% - 77px)',
    display: 'flex',
    justifyContent: 'center',
  },
  app__content__inner: {
    width: '100%',
    background: '#f6f6f8',
    borderRadius: '15px',
    padding: '1rem',
  },
  customer__name: {
    fontWeight: 'bold',
    fontSize: '16px',
    lineHeight: '19px',
    color: '#171717',
  },
  vehicle__details: {
    fontWeight: '600',
    fontSize: '13px',
    lineHeight: '17px',
    color: '#8f92a1',
    paddingTop: '1rem',
    '& .dot': {
      color: '#c4c4c4',
      padding: '. .5rem',
    },
    '& .car__model': {
      color: '#29264a',
    },
  },
  btn__width: {
    width: '215px',
    alignSelf: 'center',
  },
}));

export default function Calendar({ matchesMedia }) {
  const classes = useStyles();
  const [startingIndex, setStartingIndex] = useState(4);
  const [endingIndex, setEndingIndex] = useState(0);
  const history = useHistory();
  const [days, setDays] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [createAppointmentPermission, setCreateAppointmentPermission] =
    useState(true);

  useEffect(() => {
    const createAppointmentPermis = hasPermission('appointments', 'Create');

    if (createAppointmentPermis !== 'owner') {
      setCreateAppointmentPermission(createAppointmentPermis);
    }
  }, []);

  // calculating dates between current and next month date (e.g. 02-01-2022 to 02-02-2022)
  const enumerateDaysBetweenDates = () => {
    const now = Moment();
    const endDate = Moment().add(30, 'days');
    const dates = [];
    let i = 0;
    while (now.isSameOrBefore(endDate)) {
      const newDay = now.clone();
      const dayNum = newDay.format('DD');
      const date = newDay.format('YYYY-MM-DD');
      const shortName = newDay.format('ddd');

      dates.push({
        id: i,
        dayNum,
        date,
        shortName,
        selected: Moment(date, 'YYYY-MM-DD').isSame(
          Moment().format('YYYY-MM-DD'),
        ),
      });
      now.add(1, 'days');
      i += 1;
    }
    return dates;
  };

  useEffect(() => {
    const results = enumerateDaysBetweenDates();

    setDays(results);
  }, []);

  const {
    appointmentList: { appointments, loading },
  } = useSelector((s) => s.appointment);

  const dispatch = useDispatch();
  useEffect(() => {
    const userDetails = JSON.parse(localStorage.getItem('userData')) || '';

    if (!userDetails?.userStore) return; // call api only if user store exists
    dispatch(getAppointments());
  }, []);

  // filter appointment list according to date selected
  const handleFilterAppointments = (date) => {
    // date => 2022-01-02
    const filteredList = appointments.filter((ap) => {
      return ap?.start?.split(' ')?.[0] === date;
    });

    setFilteredAppointments(filteredList);
  };

  // success case of get appointments
  useEffect(() => {
    if (appointments?.length) {
      const currentDate = Moment().format('YYYY-MM-DD');
      handleFilterAppointments(currentDate);

      // find length of upcoming appointments
      const totalUpcomingApp = appointments?.filter((ap) => {
        return Moment(ap?.start?.split(' ')?.[0], 'YYYY-MM-DD').isSameOrAfter(
          currentDate,
          'YYYY-MM-DD',
        );
      });
      setUpcomingAppointments(totalUpcomingApp);
    }
  }, [appointments]);

  const routeToAppointment = () => {
    const selectedDate = days.find((d) => d.selected);
    history.push('/appointments', {
      isDashboard: true,
      date: selectedDate?.date || new Date(),
    });
  };

  const addEditAppointment = (data) => {
    const permission = hasPermission('appointments', 'View');
    if (permission === 'owner' || permission) {
      // appointment view
      history.push('/appointments', data);
    }
  };

  // handle date slider
  const handleDateSlider = (mov) => {
    switch (mov) {
      case 'next':
        if (startingIndex >= days.length) return;
        setStartingIndex((n) => n + 4);
        setEndingIndex((o) => o + 4);
        break;
      case 'back':
        if (endingIndex <= 0) return;
        setStartingIndex((n) => n - 4);
        setEndingIndex((o) => o - 4);
        break;
      default:
        setStartingIndex(4);
        setEndingIndex(0);
    }
  };

  // when user select date from calendar then show then appointments accordingly

  const handleSelectedDate = (i, date) => {
    const allDays = [...days];
    for (let n = 0; n < allDays.length; n++) {
      allDays[n].selected = false;
    }
    allDays[i].selected = !allDays[i].selected;
    setDays(allDays);
    handleFilterAppointments(date);
  };

  return (
    <>
      <div className={classes.background__style}>
        <div className={clsx(classes.header, classes.space__between)}>
          <Typography className={classes.heading}>
            {loading ? <Skeleton width={105} /> : 'Upcoming Appointments'}
          </Typography>
          {loading ? (
            <Typography className={classes.amount}>
              <Skeleton width={25} />
            </Typography>
          ) : (
            <Typography className={classes.amount}>
              {upcomingAppointments?.length < 10
                ? '0' + upcomingAppointments?.length
                : upcomingAppointments?.length}
            </Typography>
          )}
        </div>
        <div
          className={clsx(
            classes.date__select__container,
            classes.space__between,
          )}
        >
          <IconButton
            onClick={() => handleDateSlider('back')}
            className={classes.arrow__icon}
            disabled={loading}
          >
            <KeyboardArrowLeftIcon
              style={{ fontSize: matchesMedia && '2rem' }}
            />
          </IconButton>

          {days?.slice(endingIndex, startingIndex).map((d) => {
            return (
              <div
                className={clsx(
                  classes.date__day,
                  d.selected && classes.selected__date__day,
                )}
                onClick={() => handleSelectedDate(d.id, d.date)}
              >
                <div className={clsx(classes.bar__date, 'bar__texts')}>
                  {d.dayNum}
                </div>
                <div className={clsx(classes.bar__day, 'bar__texts')}>
                  {d.shortName}
                </div>
              </div>
            );
          })}
          <IconButton
            onClick={() => handleDateSlider('next')}
            className={classes.arrow__icon}
            disabled={loading}
          >
            <KeyboardArrowRightIcon
              style={{ fontSize: matchesMedia && '2rem' }}
            />
          </IconButton>
        </div>
        {filteredAppointments &&
        filteredAppointments?.length === 0 &&
        !loading ? (
          <div className={classes.empty__screen}>
            <div className={classes.empty__icon}>
              <CalendarEmpty />
            </div>
            <Typography className={classes.empty__text}>
              Once you have any upcoming appointments, they will show up here.
            </Typography>
            {createAppointmentPermission && (
              <Button
                onClick={routeToAppointment}
                className={classes.empty__book__btn}
              >
                <NewEntryNoti /> &nbsp; Book Appointment
              </Button>
            )}
          </div>
        ) : loading ? (
          <div className={classes.content__main}>
            {[0, 1, 2].map((i) => {
              return (
                <div className={classes.inner__div} key={i++}>
                  <div className={classes.time__slot}>
                    <div className={classes.time__slot__content}>
                      <Typography
                        className={clsx(classes.time__up, 'time__text')}
                      >
                        <Skeleton width={40} />
                      </Typography>
                      <Typography className={classes.separator}>
                        {/* separator */}
                      </Typography>
                      <Typography
                        className={clsx(classes.time__down, 'time__text')}
                      >
                        <Skeleton width={55} />
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.appointment__content}>
                    <div className={classes.app__content__inner}>
                      <Typography className={classes.customer__name}>
                        <Skeleton width={157} />
                      </Typography>
                      <Typography
                        className={clsx(classes.vehicle__details, 'd-flex')}
                      >
                        <Skeleton width={80} />
                      </Typography>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={classes.content__main}>
            {filteredAppointments?.map((ap) => {
              return (
                <div
                  className={classes.inner__div}
                  onClick={() => {
                    if (ap?.id) {
                      addEditAppointment({
                        id: ap.id,
                        isDashboard: true,
                      });
                    }
                  }}
                >
                  <div className={classes.time__slot}>
                    <div className={classes.time__slot__content}>
                      <Typography
                        className={clsx(classes.time__up, 'time__text')}
                      >
                        {ap?.start?.split(' ')[1]
                          ? Moment(ap?.start?.split(' ')[1], 'HH:mm').format(
                              'hh:mm A',
                            )
                          : ''}
                      </Typography>
                      <Typography className={classes.separator}>
                        {/* separator */}
                      </Typography>
                      <Typography
                        className={clsx(classes.time__down, 'time__text')}
                      >
                        {ap?.end?.split(' ')[1]
                          ? Moment(ap?.end?.split(' ')[1], 'HH:mm').format(
                              'hh:mm A',
                            )
                          : ''}
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.appointment__content}>
                    <div className={classes.app__content__inner}>
                      <Typography className={classes.customer__name}>
                        {ap?.customer?.name}
                      </Typography>
                      <Typography className={classes.vehicle__details}>
                        {ap?.vehicle?.vehicleMake} {ap?.vehicle?.vehicleModel}
                        <span className='dot'>•</span>
                        <span className='car__model'>
                          {ap?.vehicle?.licensePlate}
                        </span>
                      </Typography>
                    </div>
                  </div>
                </div>
              );
            })}
            {createAppointmentPermission && (
              <Button
                onClick={routeToAppointment}
                className={clsx(classes.empty__book__btn, classes.btn__width)}
              >
                <NewEntryNoti /> &nbsp; Book Appointment
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
